import React, { createContext, useContext, useEffect, useState } from 'react';
import { CinematicVariant, generateCinematicCSS } from '../constants/cinematicTokens';

interface CinematicThemeContextValue {
    variant: CinematicVariant;
    setVariant: (variant: CinematicVariant) => void;
    motionTier: number;
    setMotionTier: (tier: number) => void;
    isReducedMotion: boolean;
}

const CinematicThemeContext = createContext<CinematicThemeContextValue | null>(null);

interface CinematicThemeProviderProps {
    children: React.ReactNode;
    defaultVariant?: CinematicVariant;
}

const STORAGE_KEYS = {
    variant: 'cinematic.theme.variant',
    motionTier: 'cinematic.theme.motionTier'
} as const;

export function CinematicThemeProvider({
    children,
    defaultVariant = 'vibrant'
}: CinematicThemeProviderProps) {
    // Initialize variant from localStorage or default
    const [variant, setVariantState] = useState<CinematicVariant>(() => {
        if (typeof window === 'undefined') return defaultVariant;

        const stored = localStorage.getItem(STORAGE_KEYS.variant);
        if (stored && ['vibrant', 'calm', 'highContrast'].includes(stored)) {
            return stored as CinematicVariant;
        }
        return defaultVariant;
    });

    // Initialize motion tier (0-3, higher = more animations)
    const [motionTier, setMotionTierState] = useState<number>(() => {
        if (typeof window === 'undefined') return 3;

        const stored = localStorage.getItem(STORAGE_KEYS.motionTier);
        if (stored) {
            const tier = parseInt(stored, 10);
            if (tier >= 0 && tier <= 3) return tier;
        }
        return 3; // Default to maximum animations
    });

    // Detect reduced motion preference
    const [isReducedMotion, setIsReducedMotion] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        setIsReducedMotion(mediaQuery.matches);

        const handleChange = (e: MediaQueryListEvent) => {
            setIsReducedMotion(e.matches);
            // Auto-adjust motion tier when reduced motion preference changes
            if (e.matches) {
                setMotionTierState(0); // Disable all non-essential animations
            }
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    // Persist variant changes
    const setVariant = (newVariant: CinematicVariant) => {
        setVariantState(newVariant);
        if (typeof window !== 'undefined') {
            localStorage.setItem(STORAGE_KEYS.variant, newVariant);
        }
    };

    // Persist motion tier changes
    const setMotionTier = (tier: number) => {
        const clampedTier = Math.max(0, Math.min(3, tier));
        setMotionTierState(clampedTier);
        if (typeof window !== 'undefined') {
            localStorage.setItem(STORAGE_KEYS.motionTier, clampedTier.toString());
        }
    };

    // Apply variant as data attribute to document element and inject CSS
    useEffect(() => {
        if (typeof document !== 'undefined') {
            document.documentElement.setAttribute('data-cinematic-variant', variant);

            // Inject or update cinematic CSS
            let styleElement = document.getElementById('cinematic-theme-css') as HTMLStyleElement;
            if (!styleElement) {
                styleElement = document.createElement('style');
                styleElement.id = 'cinematic-theme-css';
                document.head.appendChild(styleElement);
            }

            // Generate and inject CSS for current variant
            const generatedCSS = generateCinematicCSS(variant);
            styleElement.textContent = generatedCSS;
        }
    }, [variant]);

    // Apply motion tier as data attribute
    useEffect(() => {
        if (typeof document !== 'undefined') {
            document.documentElement.setAttribute('data-motion-tier', motionTier.toString());
        }
    }, [motionTier]);

    const value: CinematicThemeContextValue = {
        variant,
        setVariant,
        motionTier,
        setMotionTier,
        isReducedMotion
    };

    return (
        <CinematicThemeContext.Provider value={value}>
            {children}
        </CinematicThemeContext.Provider>
    );
}

export function useCinematicTheme(): CinematicThemeContextValue {
    const context = useContext(CinematicThemeContext);
    if (!context) {
        throw new Error('useCinematicTheme must be used within a CinematicThemeProvider');
    }
    return context;
}

// Utility hook for checking motion tier eligibility
export function useMotionTier(requiredTier: number): boolean {
    const { motionTier, isReducedMotion, variant } = useCinematicTheme();

    // Reduced motion always disables tiers 1+
    if (isReducedMotion && requiredTier > 0) return false;

    // Calm and high contrast variants disable ambient/celebratory tiers
    if ((variant === 'calm' || variant === 'highContrast') && requiredTier >= 2) {
        return false;
    }

    return motionTier >= requiredTier;
}

// Utility hook for getting semantic colors based on current variant
export function useSemanticColors() {
    // For now, return CSS custom properties - components will use them directly
    // In future iterations, we could return the actual color values here
    return {
        bg: {
            base: 'var(--color-bg-base)',
            alt: 'var(--color-bg-alt)',
            subtle: 'var(--color-bg-subtle)'
        },
        surface: {
            raised: 'var(--color-surface-raised)',
            overlay: 'var(--color-surface-overlay)',
            glass: 'var(--color-surface-glass)',
            border: 'var(--color-surface-border)'
        },
        text: {
            primary: 'var(--color-text-primary)',
            secondary: 'var(--color-text-secondary)',
            tertiary: 'var(--color-text-tertiary)',
            inverse: 'var(--color-text-inverse)'
        },
        status: {
            positive: 'var(--color-status-positive)',
            caution: 'var(--color-status-caution)',
            risk: 'var(--color-status-risk)',
            neutral: 'var(--color-status-neutral)',
            info: 'var(--color-status-info)'
        },
        focus: {
            ring: 'var(--color-focus-ring)',
            ringGlow: 'var(--color-focus-ring-glow)'
        }
    };
}